import { isNotFound, isRedirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { toast } from "@beweegbaas/ui";
import { ServerUnavailableError } from "#/modules/util.errors/middleware.server-unavailable";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { isExpectedApiError } from "#/modules/util.errors/expected";
import { ApiFailureError, isApiFailure, translateApiError } from "#/modules/util.errors/translate";
import { op } from "#/modules/util.instrumentation/op";
import { m } from "#/paraglide/messages";

function surfaceApiFailure(key: string, message: string): never {
  void op.track("error_shown", { key });
  toast.error(message);
  throw new ApiFailureError({ key, message });
}

/**
 * Client-phase middleware for mutation server fns. An expected failure comes back as an
 * `ApiFailure` value (see `apiFailure`), or is a documented API error the handler threw: toast it
 * and throw client-side so the caller's success flow stops. Anything else thrown is unexpected:
 * toast generically and rethrow. Redirect/notFound throws pass through untouched — the router
 * owns those.
 */
export const toastErrorMiddleware = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    let result;
    try {
      result = await next();
    } catch (error) {
      if (isRedirect(error) || isNotFound(error)) throw error;
      if (isExpectedApiError(error)) surfaceApiFailure(error.title, translateApiError(error));
      captureUnexpectedError(error, "server-fn");
      void op.track("error_shown", { key: "unknown" });
      toast.error(
        ServerUnavailableError.is(error) ? error.message : m["common.something_went_wrong"](),
      );
      throw error;
    }
    if ("result" in result && isApiFailure(result.result)) {
      const { key, message } = result.result.apiError;
      surfaceApiFailure(key, message);
    }
    return result;
  },
);
